import { omit } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { FilterChipGroup } from "../../commons/filters/FilterChip.js";
import { OutgoingQuotesFilters } from "../../main-routes/documents/Quotes.js";
import { CrmCompanyFilterChip } from "../crm-companies/CrmCompanyFilterChip.js";
import { useQuoteAcceptanceTypes } from "./useAcceptanceTypes.js";
import { useQuoteBindingTypes } from "./useBindingTypes.js";
import { useQuotePublishingStatus } from "./usePublishingStatus.js";

export function useQuotesActiveFilterChips<F extends OutgoingQuotesFilters>({
  filters,
  setFilters,
}: {
  filters: F;
  setFilters?: React.Dispatch<React.SetStateAction<F>>;
}): Array<JSX.Element> {
  const { t } = useTranslate(["QuoteFilter"]);
  const { acceptanceTypeLabels } = useQuoteAcceptanceTypes();
  const { publishingStatusLabels } = useQuotePublishingStatus();
  const { bindingTypeLabels } = useQuoteBindingTypes();

  const chips: Array<JSX.Element> = [];

  if (filters.filterByDocActors && filters.filterByDocActors?.length) {
    chips.push(
      ...filters.filterByDocActors.map(actorFilter => (
        <CrmCompanyFilterChip
          key={`${actorFilter.type}-${actorFilter.crmOrganisationId}`}
          label={actorFilter.type}
          value={actorFilter.crmOrganisationId}
          onDelete={
            setFilters
              ? () =>
                  setFilters(currentFilters => {
                    const next = currentFilters.filterByDocActors?.filter(
                      filter =>
                        !(
                          filter.crmOrganisationId ===
                            actorFilter.crmOrganisationId &&
                          filter.type === actorFilter.type
                        )
                    );

                    return next && next.length > 0
                      ? {
                          ...currentFilters,
                          filterByDocActors: next,
                        }
                      : (omit(currentFilters, "filterByDocActors") as F);
                  })
              : undefined
          }
        />
      ))
    );
  }

  if (filters.filterAgreement) {
    chips.push(
      <FilterChipGroup
        key="agreement"
        label={t("Acceptance", { ns: "QuoteFilter" })}
        values={filters.filterAgreement}
        setValues={
          setFilters
            ? value =>
                value && value.length > 0
                  ? setFilters(filters => ({
                      ...filters,
                      filterAgreement: value,
                    }))
                  : setFilters(filters => omit(filters, "filterAgreement") as F)
            : undefined
        }
        getValueLabel={value => acceptanceTypeLabels[value]}
      />
    );
  }

  if (filters.filterIsPublished) {
    chips.push(
      <FilterChipGroup
        key="publishing"
        label={t("Publishing status", { ns: "QuoteFilter" })}
        values={filters.filterIsPublished}
        setValues={
          setFilters
            ? value =>
                value && value.length > 0
                  ? setFilters(filters => ({
                      ...filters,
                      filterIsPublished: value,
                    }))
                  : setFilters(
                      filters => omit(filters, "filterIsPublished") as F
                    )
            : undefined
        }
        getValueLabel={value => publishingStatusLabels[value]}
      />
    );
  }

  if (filters.filterIsBinding) {
    chips.push(
      <FilterChipGroup
        key="binding"
        label={t("Binding type", { ns: "QuoteFilter" })}
        values={filters.filterIsBinding}
        setValues={
          setFilters
            ? value =>
                value && value.length > 0
                  ? setFilters(filters => ({
                      ...filters,
                      filterIsBinding: value,
                    }))
                  : setFilters(filters => omit(filters, "filterIsBinding") as F)
            : undefined
        }
        getValueLabel={value => bindingTypeLabels[value]}
      />
    );
  }

  return chips;
}
